import { hashKey, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { getTokenAmount } from '@/components/TransactionModule/MarketMode/utils';

import {
  getAdvancedOrderProvider,
  isLimitOrderSupportedChainId,
  isTriggerOrderType,
  type AdvancedOrderType,
} from '@/constants/advancedOrder';

import type { LimitOrderExpiration } from '@/store/limitOrder';

import type { TokenResult } from '@/utils/0x/token-registry.types';

import {
  advancedOrderQuoteKey,
  advancedOrderQuoteRetryOptions,
  fetchAdvancedOrderEstimate,
  type AdvancedOrderQuoteInputs,
} from './advancedOrderQuote';
import type { TaggedLimitRate } from './limitRateStore';
import { deriveAdvancedTradeQuote, type AdvancedTradeQuote } from './tradeQuote';

export type AdvancedTradeQuoteParams = {
  /** False while another lane owns the trade; no Definitive fetch fires. */
  enabled: boolean;
  /** Whether a pair and a positive sell amount are selected. */
  hasTradeInputs: boolean;
  taggedLimitRate: TaggedLimitRate;
  pairKey: string;
  sellInputDecimal: string;
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  /** The wallet on the pair's chain; undefined quotes an unsigned preview. */
  maker: string | undefined;
  expiration: LimitOrderExpiration;
  customExpiration: Date | undefined;
  orderType: AdvancedOrderType;
};

const QUOTE_DEBOUNCE_MS = 300;

/**
 * Advanced lane provider: derives the gross order terms from the rate, then
 * asks Definitive (via build-order) for the net-of-fees output and fee total
 * for exactly those terms. The request is debounced and keyed on every input
 * that changes the quote (amounts, tokens, wallet, expiry, order type), so an
 * edit is reported as `loading` rather than served from a superseded estimate.
 */
export function useAdvancedTradeQuote({
  enabled,
  hasTradeInputs,
  taggedLimitRate,
  pairKey,
  sellInputDecimal,
  sellToken,
  buyToken,
  maker,
  expiration,
  customExpiration,
  orderType,
}: AdvancedTradeQuoteParams): AdvancedTradeQuote {
  const derived = useMemo(
    () =>
      deriveAdvancedTradeQuote({
        active: enabled && hasTradeInputs,
        taggedLimitRate,
        pairKey,
        sellInputDecimal,
        buyToken,
      }),
    [buyToken, enabled, hasTradeInputs, pairKey, sellInputDecimal, taggedLimitRate],
  );

  const chainId = sellToken?.chainId;
  const provider =
    chainId !== undefined && isLimitOrderSupportedChainId(chainId)
      ? getAdvancedOrderProvider(chainId)
      : undefined;
  const customExpirationMs = customExpiration?.getTime();

  const inputs = useMemo<AdvancedOrderQuoteInputs | null>(() => {
    if (provider !== 'definitive' || derived.status !== 'derived' || !derived.buyAmount) {
      return null;
    }
    if (!sellToken || !buyToken) return null;
    const sellAmount = getTokenAmount(sellInputDecimal, sellToken.decimals);
    if (!sellAmount) return null;
    return {
      chainId: sellToken.chainId,
      maker,
      sellToken: { address: sellToken.address, decimals: sellToken.decimals },
      buyToken: { address: buyToken.address, decimals: buyToken.decimals },
      sellAmount,
      buyAmount: derived.buyAmount,
      expiration,
      customExpiration: customExpirationMs,
      orderType,
      triggerCrossPrice: isTriggerOrderType(orderType) ? taggedLimitRate.rate.sell : undefined,
    };
  }, [
    buyToken,
    customExpirationMs,
    derived.buyAmount,
    derived.status,
    expiration,
    maker,
    orderType,
    provider,
    sellInputDecimal,
    sellToken,
    taggedLimitRate.rate.sell,
  ]);

  // Compare by content, not identity: upstream token objects may be re-created without changing the request.
  const inputsHash = hashKey(advancedOrderQuoteKey(inputs));
  const [debounced, setDebounced] = useState({ hash: inputsHash, inputs });
  useDebounce(() => setDebounced({ hash: inputsHash, inputs }), QUOTE_DEBOUNCE_MS, [inputsHash]);
  const debouncedInputs = debounced.inputs;

  const query = useQuery({
    queryKey: advancedOrderQuoteKey(debouncedInputs),
    queryFn: () => fetchAdvancedOrderEstimate(debouncedInputs!),
    enabled: debouncedInputs !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Refresh when the server quote context lapses so a shown estimate always backs a live payload.
    // Anchored on dataUpdatedAt (not now) so the value is stable across renders and the timer is not reset.
    refetchInterval: (query) => {
      const build = query.state.data?.build;
      if (!build?.signable) return false;
      return Math.max(build.expiresAt - query.state.dataUpdatedAt, 1000);
    },
    ...advancedOrderQuoteRetryOptions,
  });

  return useMemo<AdvancedTradeQuote>(() => {
    if (inputs === null) return derived;
    const settled = debounced.hash === inputsHash;
    if (settled && query.data) {
      return { ...derived, estimate: query.data, isStale: query.isFetching };
    }
    if (settled && query.error) {
      return { ...derived, status: 'error', error: query.error };
    }
    return { ...derived, status: 'loading' };
  }, [debounced.hash, derived, inputs, inputsHash, query.data, query.error, query.isFetching]);
}
